import React, { useEffect, useState } from "react";
import { Modal, Input, Switch, Icon } from "../../";
import { isMyZoneForZone, typeOptions } from "./BusinessZones.utils";

import BusinessZoneCircleMap from "./BusinessZoneCircleMap";
import BusinessZonePolygonMap from "./BusinessZonePolygonMap";
import { useBusinessesLite } from "../../../../hooks";

const BusinessZoneModal = ({
  isOpen,
  onClose,
  editingZone,
  zoneForm,
  setZoneForm,
  businessAddress,
  businessLocation,
  businessId,
  onSubmitZone,
  onUpdateZoneBusinesses,
}) => {
  const [tab, setTab] = useState("details");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const { data: businesses = [], isLoading: isLoadingBusinesses } =
    useBusinessesLite({
      enabled: isOpen && tab === "share",
      searchTerm,
    });
  const [selectedBusinessIds, setSelectedBusinessIds] = useState(new Set());
  const [savingShare, setSavingShare] = useState(false);

  // Filter out the current business from the list
  const filteredBusinesses = React.useMemo(() => {
    if (!businessId) return businesses;
    return businesses.filter((b) => b.id !== parseInt(businessId, 10));
  }, [businesses, businessId]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isOpen && tab === "share" && editingZone) {
      const current = new Set();
      (editingZone.businesses || []).forEach((b) => current.add(b.id));
      setSelectedBusinessIds(current);
    }
  }, [isOpen, tab, editingZone]);

  const toggleBusinessSelected = (id, enabled) => {
    setSelectedBusinessIds((prev) => {
      const next = new Set(prev);
      if (enabled) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    const all = new Set(filteredBusinesses.map((b) => b.id));
    setSelectedBusinessIds(all);
  };

  const handleSelectNone = () => {
    setSelectedBusinessIds(new Set());
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={editingZone ? "Edit Zone" : "Create Zone"}
      subtitle={
        editingZone ? "Update delivery zone" : "Add a new delivery zone"
      }
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        <div className="border-b border-gray-800">
          <nav className="flex space-x-6 overflow-x-auto">
            <button
              onClick={() => setTab("details")}
              className={`border-b-2 px-1 py-3 text-sm font-medium whitespace-nowrap transition-colors ${tab === "details" ? "border-blue-500 text-white" : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"}`}
            >
              Details
            </button>
            <button
              onClick={() => setTab("share")}
              className={`border-b-2 px-1 py-3 text-sm font-medium whitespace-nowrap transition-colors ${tab === "share" ? "border-blue-500 text-white" : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"}`}
            >
              Share with
            </button>
          </nav>
        </div>

        {tab === "details" ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-300">
                  Name *
                </label>
                {(() => {
                  const isMyZone = isMyZoneForZone(editingZone);
                  return (
                    <Input
                      value={zoneForm.name}
                      onChange={(e) =>
                        setZoneForm((s) => ({ ...s, name: e.target.value }))
                      }
                      placeholder="Zone name"
                      disabled={!!editingZone && !isMyZone}
                    />
                  );
                })()}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-300">
                  Type *
                </label>
                {(() => {
                  const isMyZone = isMyZoneForZone(editingZone);
                  const allowed = [1, 2, 4];
                  const isEditingLegacy =
                    !!editingZone && [5].includes(editingZone.type);
                  const baseOptions = typeOptions.filter((o) =>
                    allowed.includes(o.value),
                  );
                  const legacyOption = isEditingLegacy
                    ? typeOptions.find((o) => o.value === editingZone.type)
                    : null;
                  return (
                    <select
                      className="custom-select block h-10 w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed"
                      value={zoneForm.type}
                      onChange={(e) =>
                        setZoneForm((s) => ({
                          ...s,
                          type: Number(e.target.value),
                          data: null,
                        }))
                      }
                      disabled={!!editingZone && !isMyZone}
                    >
                      {legacyOption ? (
                        <option value={legacyOption.value} disabled>
                          {legacyOption.content} (current)
                        </option>
                      ) : null}
                      {baseOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.content}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-300">
                  Minimum *
                </label>
                <Input
                  value={zoneForm.minimum}
                  onChange={(e) =>
                    setZoneForm((s) => ({
                      ...s,
                      minimum: e.target.value.replace(/\D/g, "").slice(0, 15),
                    }))
                  }
                  placeholder="e.g. 1"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-300">
                  Price *
                </label>
                <Input
                  value={zoneForm.price}
                  onChange={(e) =>
                    setZoneForm((s) => ({
                      ...s,
                      price: e.target.value.replace(/\D/g, "").slice(0, 15),
                    }))
                  }
                  placeholder="e.g. 1"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-300">
                Business address
              </label>
              <Input
                value={businessAddress || ""}
                onChange={() => {}}
                disabled
              />
            </div>

            {Number(zoneForm.type) === 1 ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Select circle area
                </label>
                <BusinessZoneCircleMap
                  apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  center={{
                    lat: businessLocation?.lat,
                    lng: businessLocation?.lng,
                  }}
                  value={zoneForm.data}
                  onChange={(val) => setZoneForm((s) => ({ ...s, data: val }))}
                />
              </div>
            ) : null}

            {Number(zoneForm.type) === 2 ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Draw polygon area
                </label>
                <BusinessZonePolygonMap
                  apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  center={{
                    lat: businessLocation?.lat,
                    lng: businessLocation?.lng,
                  }}
                  value={zoneForm.data}
                  onChange={(val) => setZoneForm((s) => ({ ...s, data: val }))}
                />
              </div>
            ) : null}

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="rounded border border-gray-600 bg-gray-700 px-4 py-2 text-sm text-white"
              >
                Cancel
              </button>
              {(() => {
                const isType1 = Number(zoneForm.type) === 1;
                const isType2 = Number(zoneForm.type) === 2;
                const polygonData = zoneForm?.data || [];

                const hasValidData =
                  (!isType1 && !isType2) ||
                  (isType1 && zoneForm.data?.center) ||
                  (isType2 && polygonData && polygonData.length >= 3);

                const isDisabled =
                  !zoneForm.name.trim() ||
                  !zoneForm.type ||
                  zoneForm.minimum === "" ||
                  zoneForm.price === "" ||
                  ((isType1 || isType2) && !hasValidData);

                return (
                  <button
                    onClick={onSubmitZone}
                    disabled={isDisabled}
                    className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {editingZone ? "Save changes" : "Create zone"}
                  </button>
                );
              })()}
            </div>
          </div>
        ) : null}

        {tab === "share" ? (
          <div className="relative space-y-4">
            {!editingZone ? (
              <div className="text-sm text-gray-400">
                Save the zone first to share it with other businesses.
              </div>
            ) : (
              <>
                {/* Search Input */}
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Icon name="Search" className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search businesses by name..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                  {searchInput && (
                    <button
                      onClick={() => setSearchInput("")}
                      className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500"
                      type="button"
                    >
                      <svg
                        className="h-3 w-3 text-gray-300 hover:text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-300">
                    Toggle businesses to share this zone
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleSelectAll}
                      className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-blue-400 transition-colors hover:text-blue-300"
                    >
                      <Icon name="CheckSquare" className="h-4 w-4" />
                      <span>Select All</span>
                    </button>
                    <button
                      onClick={handleSelectNone}
                      className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-400 transition-colors hover:text-gray-300"
                    >
                      <Icon name="Square" className="h-4 w-4" />
                      <span>Select None</span>
                    </button>
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl border border-gray-700 bg-gray-800/60">
                  {isLoadingBusinesses ? (
                    <div className="p-6 text-center text-gray-300">
                      <div className="mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500"></div>
                      Loading businesses...
                    </div>
                  ) : filteredBusinesses.length === 0 ? (
                    <div className="p-6 text-center text-gray-300">
                      No businesses found
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-700">
                      {filteredBusinesses.map((b) => {
                        const checked = selectedBusinessIds.has(b.id);
                        return (
                          <div
                            key={b.id}
                            className="flex items-center justify-between px-4 py-3"
                          >
                            <div className="flex items-center space-x-3">
                              {b.logo ? (
                                <img
                                  src={b.logo}
                                  alt={b.name}
                                  className="h-8 w-8 rounded object-cover"
                                />
                              ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-700">
                                  <Icon
                                    name="Store"
                                    className="h-4 w-4 text-gray-400"
                                  />
                                </div>
                              )}
                              <div className="text-sm text-white">{b.name}</div>
                            </div>
                            <Switch
                              checked={checked}
                              onCheckedChange={(v) =>
                                toggleBusinessSelected(b.id, v)
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="sticky bottom-0 z-10">
                  <div className="-mx-4 mt-4 flex justify-end border-t border-gray-700 bg-gray-900/80 px-4 py-3 backdrop-blur">
                    <button
                      onClick={async () => {
                        if (!editingZone || savingShare) return;
                        try {
                          setSavingShare(true);
                          await onUpdateZoneBusinesses?.(
                            editingZone.id,
                            Array.from(selectedBusinessIds),
                          );
                        } finally {
                          setSavingShare(false);
                        }
                      }}
                      disabled={savingShare}
                      className="flex items-center justify-center rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {savingShare ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      ) : (
                        "Save sharing"
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default BusinessZoneModal;
